import * as React from 'react';

import { ApplicationSetting, CurrentUser } from '../models';
import { useApplicationSettingsQuery, useCurrentUserQuery, useTeamMemberForDataRoomQuery } from '../hooks';

type AuthContext = {
	authToken: string;
	isAuthenticated: boolean;
	isLoading: boolean;
	isTeamMember: boolean;
	currentUser: CurrentUser;
	settings: ApplicationSetting[] | undefined;
};

const Context = React.createContext<AuthContext | null>(null);

export const AuthProvider: React.FC = ({ children }) => {
	const urlGuid = getGuidFromUrl(window.location.href);
	const apiAuthCookie = document.cookie?.split('; ').find(cookie => cookie.includes('CerebroCapitalApiAuth'));
	const authToken = apiAuthCookie ? apiAuthCookie.split('=')[1] : '';

	const isAuthenticated = authToken !== '' && apiAuthCookie != undefined;

	const currentUserQuery = useCurrentUserQuery(authToken, { enabled: isAuthenticated });
	const isTeamMemberQuery = useTeamMemberForDataRoomQuery(authToken, urlGuid);
	const settingsQuery = useApplicationSettingsQuery(authToken, { enabled: isAuthenticated });

	const context: AuthContext = {
		authToken,
		isAuthenticated,
		isLoading: currentUserQuery.isLoading || settingsQuery.isLoading || isTeamMemberQuery.isLoading,
		currentUser: currentUserQuery.data,
		settings: settingsQuery.data,
		isTeamMember: isTeamMemberQuery.data ?? false,
	};

	return <Context.Provider value={context}>{children}</Context.Provider>;
};

function getGuidFromUrl(url: string): string | null {
	const guidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
	const match = guidRegex.exec(url);
	return match ? match[0] : null;
}

export function useAuth() {
	var context = React.useContext(Context);

	if (context == null) throw Error('You tried to consume the AuthContext outside of an AuthProvider!');

	return context;
}
